/**
 * A foto da carteirinha pelo TanStack Query: a imagem pronta para o `<img>`, o
 * envio e a exclusão.
 *
 * Papel: aqui ficam `useFotoDaCarteirinha`, que entrega a imagem de uma apólice
 * como endereço `blob:` de vida controlada, e as duas mutações,
 * `useEnviarFotoDaCarteirinha` e `useExcluirFotoDaCarteirinha`, que gravam e
 * mandam a lista de apólices se atualizar.
 *
 * Como usar: o cache do Query guarda o `Blob`, e não o endereço. Cada
 * componente que mostra a imagem recebe o endereço por `useFotoDaCarteirinha`,
 * e quem o cria e revoga é `URLS_DOS_BLOBS`, contando quantos componentes
 * montados ainda o usam. Chamar `URL.createObjectURL` direto num componente
 * deixaria o endereço vivo até a página fechar, e revogá-lo na desmontagem
 * quebraria a imagem de outro componente que mostrasse o mesmo `Blob` — a
 * tabela e o formulário mostram a mesma foto ao mesmo tempo.
 *
 * A chave leva `foto.atualizadaEm`: trocar a foto muda o instante, a lista de
 * apólices volta com o instante novo e a chave nova busca a imagem nova, sem
 * invalidação nenhuma no ramo da foto. A chave não começa por `CHAVE_PLANOS`
 * de propósito, para que salvar uma apólice — que invalida aquele ramo — não
 * baixe de novo todas as imagens da tabela.
 *
 * A revogação espera a volta do laço de eventos. No `StrictMode` o React monta,
 * desmonta e monta de novo cada componente, e uma revogação imediata
 * invalidaria, entre a segunda e a terceira passada, o endereço que a imagem
 * acabou de receber.
 */
import { type NovaFotoDaCarteirinha, type PlanoSaude } from '@concierge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import {
  buscarFotoDaCarteirinha,
  enviarFotoDaCarteirinha,
  excluirFotoDaCarteirinha,
} from '../api/planos-saude';
import { CHAVE_PLANOS, CHAVE_SAUDE } from './usar-saude';

/** Por quanto tempo a imagem baixada vale sem nova requisição: cinco minutos. */
const VALIDADE_DA_FOTO_EM_MS = 5 * 60 * 1000;

/** O endereço `blob:` de um `Blob` e quantos componentes montados o usam. */
interface UrlDoBlob {
  url: string | null;
  usos: number;
  revogacao: ReturnType<typeof setTimeout> | null;
}

/**
 * Os endereços criados, um por `Blob`.
 *
 * É um `WeakMap` para que o `Blob` que saiu do cache do Query e de todo
 * componente possa ser recolhido junto com a entrada dele.
 */
const URLS_DOS_BLOBS = new WeakMap<Blob, UrlDoBlob>();

/**
 * Passa a usar o endereço de um `Blob`, criando-o quando ainda não existe.
 *
 * @param blob - A imagem que o componente vai mostrar.
 * @param avisar - Chamado quando o endereço acaba de ser criado, para o
 *   componente desenhar de novo com ele.
 * @returns A função que deixa de usar o endereço, e que agenda a revogação
 *   quando ninguém mais o usa.
 */
function usarUrlDoBlob(blob: Blob, avisar: () => void): () => void {
  let entrada = URLS_DOS_BLOBS.get(blob);
  if (entrada === undefined) {
    entrada = { url: null, usos: 0, revogacao: null };
    URLS_DOS_BLOBS.set(blob, entrada);
  }

  const registro = entrada;
  registro.usos += 1;
  if (registro.revogacao !== null) {
    clearTimeout(registro.revogacao);
    registro.revogacao = null;
  }
  if (registro.url === null) {
    registro.url = URL.createObjectURL(blob);
    avisar();
  }

  return () => {
    registro.usos -= 1;
    if (registro.usos > 0) return;

    registro.revogacao = setTimeout(() => {
      registro.revogacao = null;
      if (registro.usos > 0 || registro.url === null) return;
      URL.revokeObjectURL(registro.url);
      registro.url = null;
    }, 0);
  };
}

/** Assinatura que não assina nada, para quando ainda não há `Blob`. */
function semAssinatura(): () => void {
  return () => undefined;
}

/** O que `useFotoDaCarteirinha` devolve. */
export interface FotoDaCarteirinhaNaTela {
  /** O endereço `blob:` para o `src` e o `href`, ou `null` sem imagem pronta. */
  url: string | null;
  /** Verdadeiro enquanto a imagem é baixada ou o endereço ainda não existe. */
  carregando: boolean;
  /** Verdadeiro quando a API recusou ou a rede falhou ao baixar a imagem. */
  falhou: boolean;
  /**
   * A imagem em si, junto com `url`, para quem precisa dos bytes — a cópia
   * para a área de transferência. Nunca vira endereço fora de `url`.
   */
  blob: Blob | null;
}

/**
 * Busca a foto de uma apólice e a entrega como endereço para o `<img>`.
 *
 * Quem chama são a `TabelaPlanos`, uma vez por linha, o `FormularioPlano`,
 * para a foto já gravada, e o `ModalDaCarteirinha`, para a imagem grande. A
 * requisição só sai quando `plano.foto` existe; sem foto, o hook devolve tudo
 * falso e nulo sem ir à API.
 *
 * @param plano - A apólice, ou `null` quando não há apólice gravada — o
 *   formulário de um plano novo.
 * @returns O endereço e o `Blob` da imagem, e os dois estados intermediários;
 *   `blob` só vem preenchido quando `url` também vem.
 */
export function useFotoDaCarteirinha(
  plano: Pick<PlanoSaude, 'id' | 'foto'> | null,
): FotoDaCarteirinhaNaTela {
  const id = plano?.id ?? null;
  const atualizadaEm = plano?.foto?.atualizadaEm ?? null;

  const consulta = useQuery({
    queryKey: [...CHAVE_SAUDE, 'foto-da-carteirinha', id, atualizadaEm],
    queryFn: ({ signal }) => buscarFotoDaCarteirinha(id ?? '', signal),
    enabled: id !== null && atualizadaEm !== null,
    staleTime: VALIDADE_DA_FOTO_EM_MS,
  });

  const blob = consulta.data ?? null;
  const assinar = useCallback(
    (avisar: () => void) => (blob === null ? semAssinatura() : usarUrlDoBlob(blob, avisar)),
    [blob],
  );
  const lerUrl = useCallback(
    () => (blob === null ? null : (URLS_DOS_BLOBS.get(blob)?.url ?? null)),
    [blob],
  );
  const url = useSyncExternalStore(assinar, lerUrl);

  if (atualizadaEm === null)
    return { url: null, carregando: false, falhou: false, blob: null };
  if (consulta.isError) return { url: null, carregando: false, falhou: true, blob: null };
  return { url, carregando: url === null, falhou: false, blob: url === null ? null : blob };
}

/** Variáveis de `useEnviarFotoDaCarteirinha`. */
interface EnviarFotoVariaveis {
  /** Identificador da apólice, que já precisa estar gravada. */
  id: string;
  dados: NovaFotoDaCarteirinha;
}

/**
 * Grava ou troca a foto da carteirinha de uma apólice.
 *
 * O `mutateAsync` rejeita com `ErroDaApi`: 400 com `campos.foto` quando a
 * imagem não passa na conferência da API, 404 quando a apólice deixou de
 * existir e 503 quando o armazenamento de fotos não está configurado, com a
 * mensagem do servidor.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto o envio não responde.
 */
export function useEnviarFotoDaCarteirinha() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dados }: EnviarFotoVariaveis) => enviarFotoDaCarteirinha(id, dados),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PLANOS }),
  });
}

/**
 * Apaga a foto da carteirinha de uma apólice.
 *
 * @returns A mutação; `isPending` verdadeiro enquanto a exclusão não responde.
 */
export function useExcluirFotoDaCarteirinha() {
  const clienteQuery = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => excluirFotoDaCarteirinha(id),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: CHAVE_PLANOS }),
  });
}
